// dependencies
import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { z } from "zod";

import { toast } from "@performyard/py-components";

// components
import { AdminTitle } from "../../../../../../../components/Admin";
import Button from "../../../../../../../components/Button";
import ColorPicker, { getNearestColor } from "../../../../../../../components/ColorPicker";
import { Grid, GridCol } from "../../../../../../../components/Grid";
import TextInput from "../../../../../../../components/Inputs/InputText";
import Spacer from "../../../../../../../components/Spacer";
import Spinner from "../../../../../../../components/Spinner/ComponentLoader";
import Text from "../../../../../../../components/Text";

// api
import { useCohort } from "../../../../../../../api/hooks";
// hooks
import useForm from "../../../../../../../hooks/useForm";
import useNavigate from "../../../../../../../hooks/useNavigate";

import { updateCohort } from "../../../../../../../api/services";

// utils
import { Hex } from "../../../../../../../utils/getContrast";

// constants
import { MAX_COHORT_NAME_LENGTH } from "../../../EngagementSetupConstants";
// styles
import css from "./SetupForm.module.scss";

const formSchema = z.object({
	name: z.string().min(1).max(100),
	description: z.string().min(0).max(1000),
	color: z.string().min(1).max(7),
});

const SetupForm = () => {
	const navigate = useNavigate();
	const { cohortID } = useParams<{ cohortID: string }>();

	if (!cohortID) return;

	const { data } = useCohort(cohortID);

	const { state, updateForm, receivedInitialState, validate, setNewDefaultValues } = useForm({
		schema: formSchema,
		defaultValues: {
			name: data?.cohort.name,
			color: data?.cohort.color,
			description: data?.cohort.description,
		},
	});

	const validation = useMemo(() => validate(), [state]);

	const handleChange = (name, value) => {
		updateForm({
			[name]: value,
		});
	};

	const handleSave = async () => {
		try {
			const newState = {
				name: state.name,
				color: state.color,
				description: state.description,
			};

			setNewDefaultValues(newState);
			await updateCohort(cohortID, newState);

			toast.success("Cohort updated successfully");
			navigate(`/administration/engagement/cohorts/${cohortID}`);
		} catch (error) {
			toast.error((error as Error).message);
		}
	};

	if (!receivedInitialState) {
		return <Spinner />;
	}

	return (
		<div className={css.main}>
			<AdminTitle includeSpacer={false}>Employee Cohorts</AdminTitle>
			<Spacer height={4} />
			<div className={css.header}>
				<Text size="paragraph-1" weight="semi-bold" color="neutral-9">
					Edit Cohort
				</Text>
			</div>

			<Spacer height={7} />

			<div className={css.formWrapper}>
				<div className={css.inputGroup}>
					<TextInput
						label="Cohort Name"
						value={state.name}
						className={css.input}
						placeholder="Insert cohort name"
						inputId="cohort-name"
						onChange={(value) => handleChange("name", value)}
						maxLength={MAX_COHORT_NAME_LENGTH}
						errorMessage={validation.errors.name?.message ?? undefined}
					/>
				</div>

				<div className={css.inputGroup}>
					<ColorPicker
						label="Select a Color"
						value={state.color as Hex}
						onChange={(value) => handleChange("color", value)}
						onInvalidColor={(value) => handleChange("color", getNearestColor(value))}
					/>
				</div>

				<div className={css.inputGroup}>
					<TextInput
						label="Description"
						value={state.description}
						className={css.input}
						placeholder="Insert cohort description"
						inputId="cohort-description"
						onChange={(value) => handleChange("description", value)}
						maxLength={1000}
						errorMessage={validation.errors.description?.message ?? undefined}
					/>
				</div>
			</div>

			<Spacer height={7} />

			<Grid gap={1} cols={5} className={css.buttonWrapper}>
				<GridCol span={2}>
					<Button href={`/administration/engagement/cohorts/${cohortID}`} className={css.button} color="blue">
						Back
					</Button>
				</GridCol>

				<GridCol span={3}>
					<Button disabled={!validation.isValid} onClick={handleSave} className={css.button} color="green">
						Save Changes
					</Button>
				</GridCol>
			</Grid>
		</div>
	);
};

export default SetupForm;
